import { apiService } from './api';

export interface Tax {
  taxId: number;
  taxCode: string;
  taxName: string;
  taxType: string;
  taxPercentage: number;
  taxCalculationMethod: string;
  applicableFor: string;
  country?: string;
  stateProvince?: string;
  effectiveFromDate?: string;
  effectiveToDate?: string;
  taxAuthorityName?: string;
  taxRegistrationNumber?: string;
  isCompoundTax: boolean;
  parentTaxId?: number;
  parentTaxName?: string;
  isActive: boolean;
  description?: string;
  createdDate: string;
}

export interface CreateTaxDTO {
  taxCode: string;
  taxName: string;
  taxType: string;
  taxPercentage: number;
  taxCalculationMethod: string;
  applicableFor: string;
  country?: string;
  stateProvince?: string;
  effectiveFromDate?: string;
  effectiveToDate?: string;
  taxAuthorityName?: string;
  taxRegistrationNumber?: string;
  isCompoundTax: boolean;
  parentTaxId?: number;
  description?: string;
}

export interface UpdateTaxDTO {
  taxCode: string;
  taxName: string;
  taxType: string;
  taxPercentage: number;
  taxCalculationMethod: string;
  applicableFor: string;
  country?: string;
  stateProvince?: string;
  effectiveFromDate?: string;
  effectiveToDate?: string;
  taxAuthorityName?: string;
  taxRegistrationNumber?: string;
  isCompoundTax: boolean;
  parentTaxId?: number;
  isActive: boolean;
  description?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
  };
}

class TaxService {
  async getTaxes(pageNumber: number = 1, pageSize: number = 50) {
    try {
      const response = await apiService.get<PaginatedResponse<Tax>>(
        '/taxes',
        { pageNumber, pageSize }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching taxes:', error);
      throw error;
    }
  }

  async getTaxById(id: number) {
    try {
      const response = await apiService.get<Tax>(`/taxes/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching tax:', error);
      throw error;
    }
  }

  async getTaxByCode(code: string) {
    try {
      const response = await apiService.get<Tax>(`/taxes/code/${code}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching tax by code:', error);
      throw error;
    }
  }

  async getTaxesByType(taxType: string) {
    try {
      const response = await apiService.get<Tax[]>(`/taxes/type/${taxType}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching taxes by type:', error);
      throw error;
    }
  }

  async getTaxesByCountry(country: string) {
    try {
      const response = await apiService.get<Tax[]>(`/taxes/country/${country}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching taxes by country:', error);
      throw error;
    }
  }

  async createTax(dto: CreateTaxDTO) {
    try {
      const response = await apiService.post<Tax>('/taxes', dto);
      return response.data.data;
    } catch (error) {
      console.error('Error creating tax:', error);
      throw error;
    }
  }

  async updateTax(id: number, dto: UpdateTaxDTO) {
    try {
      const response = await apiService.put<Tax>(`/taxes/${id}`, dto);
      return response.data.data;
    } catch (error) {
      console.error('Error updating tax:', error);
      throw error;
    }
  }

  async deleteTax(id: number) {
    try {
      await apiService.delete(`/taxes/${id}`);
    } catch (error) {
      console.error('Error deleting tax:', error);
      throw error;
    }
  }
}

const taxService = new TaxService();
export default taxService;
